import React, { useEffect, useState } from "react";
import { apiAdminCoupons, apiAdminCreateCoupon, apiAdminUpdateCoupon, apiAdminDeleteCoupon } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { code: "", type: "percent", amount: 10, minOrder: 0, maxUses: "", active: true };

const AdminCoupons = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setItems(await apiAdminCoupons());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      const payload = { ...form, maxUses: form.maxUses === "" || form.maxUses === null ? null : Number(form.maxUses) };
      if (form.id) await apiAdminUpdateCoupon(form.id, payload);
      else await apiAdminCreateCoupon(payload);
      toast.success("Coupon saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await apiAdminDeleteCoupon(id);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2"><div className="h-px w-8 bg-[#b91c1c]" /><span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Promotions</span></div>
          <h1 className="font-display text-4xl font-semibold text-[#0a0a0a] tracking-tight">Coupons</h1>
        </div>
        <Button onClick={() => setEditing({ ...empty })} className="btn-primary rounded-none h-11 px-6 text-[12px] font-semibold uppercase tracking-[0.18em]">
          <Plus className="h-4 w-4 mr-2" /> New Coupon
        </Button>
      </div>

      {loading ? <div className="text-sm text-gray-500">Loading…</div> : (
        <div className="bg-white border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-[#fafbfc] border-b border-gray-200 text-[10px] uppercase tracking-[0.18em] text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Code</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-right px-4 py-3 font-semibold">Min Order</th>
                <th className="text-right px-4 py-3 font-semibold">Uses</th>
                <th className="text-right px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-[#fafbfc]">
                  <td className="px-4 py-3 font-mono font-semibold text-[#0a0a0a]">{c.code}</td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{c.type}</td>
                  <td className="px-4 py-3 text-right font-semibold">{c.type === "percent" ? `${c.amount}%` : `$${c.amount}`}</td>
                  <td className="px-4 py-3 text-right text-gray-600">${c.minOrder}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{c.uses}{c.maxUses ? `/${c.maxUses}` : ""}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] px-2 py-1 ${c.active ? "text-green-700 bg-green-100" : "text-gray-700 bg-gray-100"}`}>
                      {c.active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => setEditing(c)} className="text-gray-500 hover:text-[#0a0a0a]"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => onDelete(c.id)} className="text-gray-500 hover:text-[#b91c1c]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-md p-0">
            <DialogHeader className="px-6 py-4 border-b border-gray-200">
              <DialogTitle className="font-display text-2xl font-semibold text-[#0a0a0a]">{editing.id ? "Edit Coupon" : "New Coupon"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); onSave(editing); }} className="p-6 space-y-4">
              <div><Label>Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} required className="mt-1 rounded-none" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="mt-1 w-full h-10 px-3 border border-gray-300 bg-white text-sm">
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div><Label>Amount</Label><Input type="number" step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: Number(e.target.value) })} className="mt-1 rounded-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Min Order ($)</Label><Input type="number" step="0.01" value={editing.minOrder} onChange={(e) => setEditing({ ...editing, minOrder: Number(e.target.value) })} className="mt-1 rounded-none" /></div>
                <div><Label>Max Uses (blank = unlimited)</Label><Input type="number" value={editing.maxUses ?? ""} onChange={(e) => setEditing({ ...editing, maxUses: e.target.value })} className="mt-1 rounded-none" /></div>
              </div>
              <div>
                <Label>Status</Label>
                <select value={editing.active ? "yes" : "no"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "yes" })} className="mt-1 w-full h-10 px-3 border border-gray-300 bg-white text-sm">
                  <option value="yes">Active</option>
                  <option value="no">Disabled</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none h-10 px-5 text-[12px] uppercase tracking-[0.18em]">Cancel</Button>
                <Button type="submit" className="btn-primary rounded-none h-10 px-5 text-[12px] uppercase tracking-[0.18em]">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminCoupons;
